import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import useAuthStore, { AgencyMembership } from "@/store/authStore";
import useGlobalStore from "@/store/globalStore";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

interface AgencySelectionProps {
  agencies: AgencyMembership[];
  preselectedAgencyId?: string;
}

/**
 * Shown after a successful multi-agency login when the backend returned
 * `requires_agency_selection = true`. The user picks which agency to enter
 * the app under; the choice is sent to POST /agency/select, persisted to the
 * store, then the user is routed to the dashboard.
 */
export default function AgencySelection({
  agencies,
  preselectedAgencyId,
}: AgencySelectionProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // A user who arrives from an email deep link and then has to pick an agency must still
  // land on the page they clicked, not the dashboard. Same same-origin guard as the login
  // form: anything that is not a single-leading-slash path is not a destination.
  const destination = (() => {
    const requested = searchParams.get("redirect");

    if (
      !requested ||
      !requested.startsWith("/") ||
      requested.startsWith("//")
    ) {
      return "/dashboard";
    }

    return requested;
  })();
  const setError = useGlobalStore((state) => state.setError);

  const { selectAgency, logout } = useAuthStore(
    useShallow((state) => ({
      selectAgency: state.selectAgency,
      logout: state.logout,
    })),
  );

  const [submittingAgencyId, setSubmittingAgencyId] = useState<string | null>(
    null,
  );
  const [highlightedAgencyId, setHighlightedAgencyId] = useState<string | null>(
    null,
  );

  // Auto-highlight preselected agency if coming from invite
  useEffect(() => {
    if (preselectedAgencyId) {
      const matchingAgency = agencies.find(
        (a) => a.agency_id === preselectedAgencyId,
      );
      if (matchingAgency) {
        setHighlightedAgencyId(preselectedAgencyId);
      }
    }
  }, [preselectedAgencyId, agencies]);

  const handleSelect = async (agencyId: string) => {
    setSubmittingAgencyId(agencyId);
    try {
      await selectAgency(agencyId);
      navigate(destination);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to select agency.";
      setError(message);
    } finally {
      setSubmittingAgencyId(null);
    }
  };

  const handleCancel = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="w-full bg-[#ea33c0] py-20 text-center">
        <h1 className="text-white text-3xl md:text-5xl font-bold">
          MediaBridge
        </h1>
      </div>

      <div className="flex flex-col items-center justify-center flex-grow py-10">
        <div className="max-w-md w-full px-5">
          <h2 className="text-2xl font-bold text-center mb-2">
            Choose an agency
          </h2>
          <p className="text-center text-gray-600 text-sm mb-6">
            You're a member of more than one agency. Select the one you'd like
            to sign in to.
          </p>

          {agencies.length === 0 ? (
            <div className="text-center text-gray-500">
              No agencies available.
            </div>
          ) : (
            <>
              {preselectedAgencyId && highlightedAgencyId && (
                <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-2 rounded text-center">
                  ✓ Your new agency is highlighted below
                </div>
              )}
              <ul className="flex flex-col gap-3">
                {agencies.map((membership) => {
                  const isSubmitting =
                    submittingAgencyId === membership.agency_id;
                  const anyInFlight = submittingAgencyId !== null;
                  const isHighlighted =
                    membership.agency_id === highlightedAgencyId;
                  return (
                    <li key={membership.agency_id}>
                      <button
                        type="button"
                        disabled={anyInFlight}
                        onClick={() => handleSelect(membership.agency_id)}
                        className={`w-full flex justify-between items-center px-4 py-3 rounded-md border text-left transition-colors ${
                          anyInFlight
                            ? "bg-gray-100 border-gray-300 cursor-not-allowed"
                            : isHighlighted
                              ? "bg-green-50 border-green-400 hover:border-green-500 ring-2 ring-green-200"
                              : "bg-white border-pink-400 hover:border-[#EA33C0] hover:bg-pink-50"
                        }`}
                      >
                        <div>
                          <div className="font-semibold text-gray-800 flex items-center gap-2">
                            {membership.agency_name || membership.agency_id}
                            {isHighlighted && (
                              <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                                New
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            Role: {membership.role}
                          </div>
                        </div>
                        {isSubmitting && <Spinner size={18} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className="text-center mt-6">
            <Button
              type="secondary"
              onClick={handleCancel}
              disabled={submittingAgencyId !== null}
              className="px-8"
            >
              Cancel & sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
